import type { UserQuery } from '../gen/user'
import {
  useLogoutMutation,
  useUserAllSubscriptionsTypesQuery,
  useUserQuery,
  useDeleteUserAccountMutation,
  usersEligibleForDeletionQueryFetcher,
} from '../gen/user'
import { AvailableCurrencyCode, CurrencyCode } from '@repo/graphql-types'
import ms from 'ms'
import { USER_FEATURES, USER_ROLES } from 'constants/user'
import { logOut } from '@repo/auth-utils'
import { keepPreviousData } from '@tanstack/react-query'

import { useLogoutNonceGetter } from './use-nonce'
import { sessionStore } from 'utils/storage'

export type User = NonNullable<NonNullable<UserQuery['me']>['user']>

interface DeleteAccountOptions {
  homeRoute: string
}

interface LogoutOptions {
  loginRoute: string
}

export const userQueryOptions = {
  placeholderData: keepPreviousData,
  staleTime: ms('15s'),
}

// eslint-disable-next-line cf-web/codegen-query-returns
export function useUser() {
  return useUserQuery(undefined, {
    ...userQueryOptions,
    select(data) {
      return data.me?.user
    },
  })
}

export function mapCurrencyToAvailableCurrencyCode(
  code: CurrencyCode | null | undefined,
): AvailableCurrencyCode {
  switch (code) {
    case CurrencyCode.Usd: {
      return AvailableCurrencyCode.Usd
    }
    case CurrencyCode.Eur: {
      return AvailableCurrencyCode.Eur
    }
    case CurrencyCode.Gbp: {
      return AvailableCurrencyCode.Gbp
    }
    default: {
      return AvailableCurrencyCode.Usd
    }
  }
}

export function useUserCurrencyQuery() {
  return useUserQuery(undefined, {
    ...userQueryOptions,
    select(data) {
      return data.me?.user?.currency
    },
  })
}

export function useUserStripeCustomerQuery() {
  return useUserQuery(undefined, {
    ...userQueryOptions,
    select(data) {
      return data.me?.user?.stripeCustomer
    },
  })
}

export function useUserIsOnHoldSubscriberQuery() {
  return useUserQuery(undefined, {
    ...userQueryOptions,
    select(data) {
      return data.me?.user?.flags?.isOnHoldSubscriber ?? false
    },
  })
}

export function useRoles() {
  const { data } = useUserQuery()
  return data?.me?.user?.roles
}

export function useUserIsDesigner() {
  const roles = useRoles()

  return roles?.includes(USER_ROLES.DESIGNER)
}

export function useUserIsTeacher() {
  const roles = useRoles()

  return roles?.includes(USER_ROLES.TEACHER)
}

export function useUserHasMyArticles() {
  const roles = useRoles()

  return roles?.includes(USER_FEATURES.MY_ARTICLES)
}

export function useHasAnySubscriptionQuery() {
  return useUserQuery(undefined, {
    ...userQueryOptions,
    select(data) {
      const access = data.me?.user?.subscriptionAccess
      return Boolean(
        access?.studioAi.isSubscribed ||
        access?.coins.isSubscribed ||
        access?.allAccess.isSubscribed ||
        access?.studioUnlimited.isSubscribed,
      )
    },
  })
}

export function useUserSubscriptionAccessQuery() {
  return useUserQuery(undefined, {
    ...userQueryOptions,
    select(data) {
      return data.me?.user?.subscriptionAccess
    },
  })
}

export function useUserAllAccessSubscriptionAccessQuery() {
  return useUserQuery(undefined, {
    ...userQueryOptions,
    select(data) {
      return data.me?.user?.subscriptionAccess?.allAccess
    },
  })
}

export function useUserStudioAiSubscriptionAccessQuery() {
  return useUserQuery(undefined, {
    ...userQueryOptions,
    select(data) {
      return data.me?.user?.subscriptionAccess?.studioAi
    },
  })
}

type UserSubscriptionAccess = NonNullable<
  NonNullable<UserQuery['me']>['user']
>['subscriptionAccess']

export type SubscriptionType = Exclude<keyof UserSubscriptionAccess, '__typename'>

export function useUserSubscriptionVariantQuery(subscriptionType: SubscriptionType) {
  return useUserQuery(undefined, {
    ...userQueryOptions,
    select: data => getUserSubscriptionVariant(data, subscriptionType),
  })
}

export function useUserSubscriptionIdQuery(subscriptionType: SubscriptionType) {
  return useUserQuery(undefined, {
    ...userQueryOptions,
    select: data => data?.me?.user?.subscriptionAccess?.[subscriptionType]?.subscription?.id,
  })
}

export function getUserSubscriptionVariant(data: UserQuery, subscriptionType: SubscriptionType) {
  const isUserLoggedIn = data?.me?.user?.id && !data.me?.user?.isTemporary
  if (!isUserLoggedIn) {
    return 'unsubscribed'
  }

  const subscriptionAccess = data.me?.user?.subscriptionAccess
  if (!subscriptionAccess) {
    return 'unsubscribed'
  }

  if (subscriptionType === 'coins') {
    return subscriptionAccess[subscriptionType].isSubscribed ? 'subscribed' : 'unsubscribed'
  }

  const subscriptionData = subscriptionAccess[subscriptionType]
  const isSubscribed = subscriptionData.isSubscribed

  if (!isSubscribed) {
    return 'unsubscribed'
  }
  if (isSubscribed && subscriptionData.subscription?.isTrial) {
    return 'onTrial'
  }
  return 'subscribed'
}

export type SubscriptionVariant = ReturnType<typeof getUserSubscriptionVariant>

export function useUserIsEligibleForAllAccessFreeTrialQuery() {
  return useUserQuery(undefined, {
    ...userQueryOptions,
    select(data) {
      return data.me?.user?.subscriptionAccess.allAccess.isEligibleForFreeTrial || false
    },
  })
}

export function useUserIsEligibleForAllAccessYearlyUpgradeQuery() {
  return useUserQuery(undefined, {
    ...userQueryOptions,
    select(data) {
      return data.me?.user?.subscriptionAccess.allAccess.isEligibleForYearlyUpgrade || false
    },
  })
}

export function useUserIsEligibleForStudioAiFreeTrialQuery() {
  return useUserQuery(undefined, {
    ...userQueryOptions,
    select(data) {
      return data.me?.user?.subscriptionAccess.studioAi.isEligibleForFreeTrial || false
    },
  })
}

export function useUserIsEligibleForStudioAiYearlyUpgradeQuery() {
  return useUserQuery(undefined, {
    ...userQueryOptions,
    select(data) {
      return data.me?.user?.subscriptionAccess.studioAi.isEligibleForYearlyUpgrade || false
    },
  })
}

export function useUserId() {
  const { data } = useUser()
  return data?.id
}

export function useHasAllAccessQuery() {
  return useUserQuery(undefined, {
    ...userQueryOptions,
    select(data) {
      return data.me?.user?.subscriptionAccess.allAccess.isSubscribed || false
    },
  })
}

export function useHasStudioAiQuery() {
  return useUserQuery(undefined, {
    ...userQueryOptions,
    select(data) {
      return data.me?.user?.subscriptionAccess.studioAi.isSubscribed || false
    },
  })
}

export function useHasStudioUnlimitedQuery() {
  return useUserQuery(undefined, {
    ...userQueryOptions,
    select(data) {
      return data.me?.user?.subscriptionAccess.studioUnlimited.isSubscribed === true
    },
  })
}

export function useCoinSubscriptionQuery() {
  return useUserQuery(undefined, {
    ...userQueryOptions,
    select(data) {
      const coins = data.me?.user?.subscriptionAccess.coins
      return {
        isSubscribed: coins?.isSubscribed ?? false,
        plan: coins?.subscription?.type ?? null,
      }
    },
  })
}

export function useIsUserAllAccessTrialingQuery() {
  return useUserQuery(undefined, {
    ...userQueryOptions,
    select(data) {
      return data?.me?.user?.subscriptionAccess.allAccess.subscription?.isTrial || false
    },
  })
}

export function useAIStudioSubscriptionIdQuery() {
  return useUserQuery(undefined, {
    ...userQueryOptions,
    select(data) {
      return data.me?.user?.subscriptionAccess.studioAi.subscription?.id
    },
  })
}

export function useAllAccessSubscriptionIdQuery() {
  return useUserQuery(undefined, {
    ...userQueryOptions,
    select(data) {
      return data.me?.user?.subscriptionAccess.allAccess.subscription?.id
    },
  })
}

export function useSubscriptionIdsQuery() {
  return useUserQuery(undefined, {
    ...userQueryOptions,
    select(data) {
      const subscriptionAccess = data.me?.user?.subscriptionAccess
      return {
        allAccess: subscriptionAccess?.allAccess.subscription?.id,
        studioAi: subscriptionAccess?.studioAi.subscription?.id,
        studioUnlimited: subscriptionAccess?.studioUnlimited.subscription?.id,
      }
    },
  })
}

export function useCoinsSubscriptionIdQuery() {
  return useUserQuery(undefined, {
    ...userQueryOptions,
    select(data) {
      return data.me?.user?.subscriptionAccess.coins.subscription?.id
    },
  })
}

export function useIsUserBanned() {
  const { data } = useUserQuery(undefined, {
    ...userQueryOptions,
    select(data) {
      return data?.me?.user?.isBanned
    },
  })
  return data
}

export function useDeleteAccount({ homeRoute }: DeleteAccountOptions) {
  const fetchIsEligibleForDeletion = usersEligibleForDeletionQueryFetcher()
  const { mutateAsync: deleteUserAccount } = useDeleteUserAccountMutation()

  const deleteAccount = async () => {
    try {
      const { usersEligibleForDeletion } = await fetchIsEligibleForDeletion()
      if (!usersEligibleForDeletion?.canBeDeleted) {
        return { error: true, message: usersEligibleForDeletion?.message }
      }
    } catch (error) {
      return { error: true }
    }
    try {
      await deleteUserAccount({})
    } catch (error) {
      return { error: true }
    }
    logOut()
    // write to sessionStorage
    sessionStore.setString('accountDeleted', 'true')
    setTimeout(() => {
      location.href = homeRoute
    }, 2000)
    return { error: false }
  }

  return deleteAccount
}

/**
 * @param redirectTo - The URL to redirect to after login successfully. If not provided, the user will be redirected to the my-account page after login.
 * @returns
 */
export function useLogout({ loginRoute }: LogoutOptions) {
  // eslint-disable-next-line cf-web/codegen-mutation-patterns
  const logout = useLogoutMutation()
  const getNonce = useLogoutNonceGetter()

  const logoutUser = async ({ redirectTo }: { redirectTo?: string } = {}) => {
    await logout.mutateAsync({})
    // Currently logout mutation is not enough to logout the user from the wordpress session we also need to get a nonce and redirect the user to the wordpress customer logout route
    const nonce = await getNonce()

    try {
      // Because after logout the page goes back to /my-account again, the NextJS app does not detect any changes.
      // We do a fetch request here to make sure we can wait until all redirects happen and the logout is successful. Only then we reload
      await fetch(`/my-account/customer-logout/?_wpnonce=${nonce}`, {
        credentials: 'include',
        redirect: 'follow',
        keepalive: true, // This is needed to make the fetch request keep the connection alive
      })
    } catch {
      // In safari, the fetch request succeeds but the response is ignored and the connection is closed. Showing a "TypeError: Failed to fetch". In Sentry,
      // The user is succesfully logged out
    }

    logOut()

    location.href = `${loginRoute}${redirectTo ? `?rd=${redirectTo}` : ''}`
  }

  return logoutUser
}

export function useActiveSubscriptionTypes() {
  const { data: subTypes } = useUserAllSubscriptionsTypesQuery(undefined, {
    select(data) {
      return data.me?.user?.subscriptions
    },
  })

  if (!subTypes) {
    return []
  }

  return Object.entries(subTypes).reduce((acc, cur) => {
    if (cur[1]) {
      acc.push(cur[0])
    }
    return acc
  }, [] as string[])
}
